/* eslint-disable @typescript-eslint/no-floating-promises */
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import {
  SubmitHandler, useForm, useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import { validateAgeMayority, validateMaxAge } from '../../../utils/validations';
import { injectCleanedFieldToForm } from '../../../utils/forms';
import { requiredString } from '../../../utils/commonSchemaValidators';

const MAX_AGE = 99;

export const assetAdvisoryFormSchema = z.object({
  name: requiredString(),
  date_of_birth: requiredString()
    .refine((date_of_birth) => validateAgeMayority(date_of_birth), { message: 'El cliente debe ser mayor de edad ' })
    .refine((date_of_birth) => validateMaxAge({ date: date_of_birth, maxAge: MAX_AGE }), { message: `La edad máxima permitida es de ${MAX_AGE} años.` }),
  profile: requiredString(),

  clp_capital_markets_investment: z.number().nonnegative().min(0),
  uf_capital_markets_investment: z.number().nonnegative().min(0),
  usd_capital_markets_investment: z.number().nonnegative().min(0),
  clp_alt_assets: z.number().nonnegative().min(0),
  uf_alt_assets: z.number().nonnegative().min(0),
  usd_alt_assets: z.number().nonnegative().min(0),
  apv: z.number().nonnegative().min(0),

  clp_pension_plan: z.number().nonnegative().min(0),
  usd_pension_plan: z.number().nonnegative().min(0),
  clp_real_state_assets: z.number().nonnegative().min(0),
  uf_real_state_assets: z.number().nonnegative().min(0),
  usd_real_state_assets: z.number().nonnegative().min(0),
  private_company_stakes: z.number().nonnegative().min(0),
  clp_other_investments: z.number().nonnegative().min(0),
  uf_other_investments: z.number().nonnegative().min(0),
  usd_other_investments: z.number().nonnegative().min(0),

  clp_mortgage_credit: z.number().nonnegative().min(0),
  uf_mortgage_credit: z.number().nonnegative().min(0),
  usd_mortgage_credit: z.number().nonnegative().min(0),
  clp_current_consumer_credit: z.number().nonnegative().min(0),
  usd_current_consumer_credit: z.number().nonnegative().min(0),
  clp_other_obligations: z.number().nonnegative().min(0),
  uf_other_obligations: z.number().nonnegative().min(0),
  usd_other_obligations: z.number().nonnegative().min(0),

  clp_incomes: z.number().nonnegative().min(0),
  uf_incomes: z.number().nonnegative().min(0),
  usd_incomes: z.number().nonnegative().min(0),
});

const isValidValue = (v: unknown) => v !== null && v !== undefined && v !== '' && !Number.isNaN(v);

export type AssetAdvisoryFormValues = z.infer<typeof assetAdvisoryFormSchema>;

export const ASSETS_SECTION_FIELDS: Array<keyof AssetAdvisoryFormValues> = [

  'clp_capital_markets_investment',
  'uf_capital_markets_investment',
  'usd_capital_markets_investment',
  'clp_alt_assets',
  'uf_alt_assets',
  'usd_alt_assets',
  'apv',

  'clp_pension_plan',
  'usd_pension_plan',
  'clp_real_state_assets',
  'usd_real_state_assets',
  'private_company_stakes',
  'clp_other_investments',
  'uf_other_investments',
  'usd_other_investments',

  'clp_mortgage_credit',
  'uf_mortgage_credit',
  'usd_mortgage_credit',
  'clp_current_consumer_credit',
  'usd_current_consumer_credit',
  'clp_other_obligations',
  'uf_other_obligations',
  'usd_other_obligations',

  'clp_incomes',
  'uf_incomes',
  'usd_incomes',
];

export function useAssetAdvisoryForm(defaultValues: AssetAdvisoryFormValues) {
  const methods = useForm<AssetAdvisoryFormValues>({
    resolver: zodResolver(assetAdvisoryFormSchema),
    defaultValues,
    mode: 'onBlur',

  });

  const [showObjectivesSection, setShowObjectivesSection] = useState(false);

  const shouldConvertNullishKeyValueToZero = (key: string, value: string | number | null) => (
    ASSETS_SECTION_FIELDS.includes(key as keyof AssetAdvisoryFormValues) && value === null
  );

  // Notice basically all fields are currency fields; so we need to clean the
  // stringified currency value (eg. $10.000.000) and transform it into a base numeric string
  // (10000000) to be able to be processed by backend.
  const injectCleanedValues = (form: HTMLFormElement) => {
    const values = methods.getValues();
    Object
      .entries(values)
      .forEach(([key, value]) => injectCleanedFieldToForm({
        form,
        value: shouldConvertNullishKeyValueToZero(key, value) ? 0 : value,
        name: key,
      }));
  };

  const handleSubmit: SubmitHandler<AssetAdvisoryFormValues> = (data, event) => {
    const form = event?.target as HTMLFormElement;
    injectCleanedValues(form);
    form.submit();
  };

  useEffect(() => {
    const verifyFieldsOnPreload = async () => {
      const isAssetsSectionValid = await methods.trigger(ASSETS_SECTION_FIELDS);
      methods.clearErrors(ASSETS_SECTION_FIELDS);
      const atLeastOneFieldFilled = methods
        .getValues(ASSETS_SECTION_FIELDS).some((v) => v !== null && v !== 0);
      methods.clearErrors(ASSETS_SECTION_FIELDS);
      if (isAssetsSectionValid && atLeastOneFieldFilled) {
        setShowObjectivesSection(true);
      }
    };

    verifyFieldsOnPreload();
  }, []);

  return {
    methods,
    showObjectivesSection,
    handleSubmit,
    injectCleanedValues,
  };
}

export function useAssetAdvisoryAccordions() {
  const clientFields = useWatch({
    name: ['name', 'date_of_birth', 'profile'],
  });

  const marketInvestmentFields = useWatch({
    name: [
      'clp_capital_markets_investment',
      'uf_capital_markets_investment',
      'usd_capital_markets_investment',
      'clp_alt_assets',
      'uf_alt_assets',
      'usd_alt_assets',
      'apv',
    ],
  });

  const otherAssetsFields = useWatch({
    name: [
      'clp_pension_plan',
      'usd_pension_plan',
      'clp_real_state_assets',
      'uf_real_state_assets',
      'usd_real_state_assets',
      'private_company_stakes',
      'clp_other_investments',
      'uf_other_investments',
      'usd_other_investments',
    ],
  });

  const obligationsFields = useWatch({
    name: [
      'clp_mortgage_credit',
      'uf_mortgage_credit',
      'usd_mortgage_credit',
      'clp_current_consumer_credit',
      'usd_current_consumer_credit',
      'clp_other_obligations',
      'uf_other_obligations',
      'usd_other_obligations',
    ],
  });

  const incomesFields = useWatch({
    name: ['clp_incomes', 'uf_incomes', 'usd_incomes'],
  });

  const allAssetFields = useWatch({
    name: ASSETS_SECTION_FIELDS,
  });

  const isClientAccordionFilled = clientFields.every(isValidValue);
  const isMarketInvestmentsAccordionFilled = marketInvestmentFields.every(isValidValue);
  const isOtherAssetsAccordionFilled = otherAssetsFields.every(isValidValue);
  const isObligationsAccordionFilled = obligationsFields.every(isValidValue);
  const isIncomesAccordionFilled = incomesFields.every(isValidValue);
  const allFieldsAreEmpty = !allAssetFields.some((v) => v !== null && v !== 0);

  return {
    isClientAccordionFilled,
    isMarketInvestmentsAccordionFilled,
    isOtherAssetsAccordionFilled,
    isObligationsAccordionFilled,
    isIncomesAccordionFilled,
    allFieldsAreEmpty,
  };
}
